import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  schedulesApi,
  sellersApi,
  type Schedule,
  type ScheduleInput,
  type ScheduleWindow,
  type SchedulePreview,
  type SellerListItem,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { WEEKDAYS_RU, fmtMskHuman } from '../lib/weekdays';
import { useBasePath, withBase } from '../lib/basePath';

// Local form state — kept in plain primitives so the controls stay simple.
type Form = {
  name: string;
  windows: ScheduleWindow[];
  ship_days: Set<number>;
  ship_time_msk: string;
  deadline_anchor: 'absolute' | 'relative';
  deadline_days: Set<number>;
  deadline_time_msk: string;
  deadline_offset_h: number;
  is_default: boolean;
  notes: string;
};

function emptyForm(): Form {
  return {
    name: '',
    windows: [],
    ship_days: new Set(),
    ship_time_msk: '10:00',
    deadline_anchor: 'absolute',
    deadline_days: new Set(),
    deadline_time_msk: '23:59',
    deadline_offset_h: 24,
    is_default: false,
    notes: '',
  };
}

function fromSchedule(s: Schedule): Form {
  return {
    name: s.name,
    windows: (s.windows ?? []).map((w) => ({
      ship_dt: w.ship_dt.slice(0, 16),                 // strip seconds + tz for datetime-local
      deadline_dt: w.deadline_dt ? w.deadline_dt.slice(0, 16) : null,
    })),
    ship_days: new Set(s.ship_days),
    ship_time_msk: s.ship_time_msk,
    deadline_anchor: s.deadline_anchor,
    deadline_days: new Set(s.deadline_days),
    deadline_time_msk: s.deadline_time_msk,
    deadline_offset_h: s.deadline_offset_h,
    is_default: s.is_default,
    notes: s.notes ?? '',
  };
}

function toInput(f: Form): Omit<ScheduleInput, 'seller_id'> {
  return {
    name: f.name.trim(),
    windows: f.windows
      .filter((w) => w.ship_dt)
      .map((w) => ({
        ship_dt: w.ship_dt,
        deadline_dt: w.deadline_dt || null,
      }))
      .sort((a, b) => a.ship_dt.localeCompare(b.ship_dt)),
    ship_days: [...f.ship_days].sort((a, b) => a - b),
    ship_time_msk: f.ship_time_msk,
    deadline_anchor: f.deadline_anchor,
    deadline_days: [...f.deadline_days].sort((a, b) => a - b),
    deadline_time_msk: f.deadline_time_msk,
    deadline_offset_h: f.deadline_offset_h,
    is_default: f.is_default,
    notes: f.notes.trim() || null,
  };
}

export default function ScheduleEdit() {
  const { id: idStr } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const base = useBasePath();
  const isNew = !idStr || idStr === 'new';
  const id = isNew ? null : Number(idStr);

  const [form, setForm] = useState<Form>(emptyForm());
  const [sellerId, setSellerId] = useState<number | null>(null);
  const [sellers, setSellers] = useState<SellerListItem[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<SchedulePreview | null>(null);

  // Load sellers list (admin) for the seller picker on create.
  useEffect(() => {
    if (user?.role === 'admin') {
      sellersApi.list().then((r) => {
        setSellers(r.sellers);
        if (isNew && !sellerId && r.sellers.length === 1) setSellerId(r.sellers[0].id);
      }).catch(() => { /* noop */ });
    } else if (user) {
      setSellerId(user.id);
    }
  }, [user, isNew]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing schedule for edit.
  useEffect(() => {
    if (isNew || !id) return;
    schedulesApi.get(id)
      .then((r) => { setForm(fromSchedule(r.schedule)); setSellerId(r.schedule.seller_id); })
      .catch((e) => setErr(String(e?.message ?? e)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  // Load preview whenever we have an existing id. We watch a stable signature
  // of the schedule shape — Set instances would change identity on every
  // toggle and re-trigger the effect infinitely.
  const previewKey = [
    [...form.ship_days].sort().join(','),
    form.ship_time_msk,
    form.deadline_anchor,
    [...form.deadline_days].sort().join(','),
    form.deadline_time_msk,
    form.deadline_offset_h,
    form.windows.map((w) => `${w.ship_dt}/${w.deadline_dt ?? ''}`).join(';'),
  ].join('|');
  useEffect(() => {
    if (isNew || !id) return;
    schedulesApi.preview(id, 5)
      .then(setPreview)
      .catch(() => { /* preview is best-effort */ });
  }, [id, isNew, previewKey]);

  function toggleSet(name: 'ship_days' | 'deadline_days', day: number) {
    setForm((f) => {
      const next = new Set(f[name]);
      if (next.has(day)) next.delete(day); else next.add(day);
      return { ...f, [name]: next };
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setSavedMsg(null);
    try {
      if (isNew) {
        if (!sellerId) throw new Error('Не выбран продавец');
        const body: ScheduleInput & { seller_id: number } = { ...toInput(form), seller_id: sellerId };
        const r = await schedulesApi.create(body);
        nav(withBase(base, `/schedules/${r.schedule.id}`), { replace: true });
      } else if (id) {
        await schedulesApi.update(id, toInput(form));
        setSavedMsg('Сохранено');
      }
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!id) return;
    if (!confirm('Удалить расписание? Товары, которые на него ссылаются, останутся без расписания.')) return;
    await schedulesApi.remove(id);
    nav(withBase(base, '/schedules'), { replace: true });
  }

  if (loading) return <div className="container"><p className="muted">Загрузка…</p></div>;

  return (
    <div className="container">
      <div className="page-head">
        <h2>{isNew ? 'Новое расписание' : `Расписание #${id}`}</h2>
        <div className="row">
          <Link to={withBase(base, '/schedules')} className="btn">← К списку</Link>
          {!isNew && <button className="btn" onClick={onDelete} type="button">Удалить</button>}
        </div>
      </div>

      <form className="card form" onSubmit={onSubmit}>
        {isNew && user?.role === 'admin' && (
          <label>
            <span>Продавец *</span>
            <select
              value={sellerId ?? ''}
              onChange={(e) => setSellerId(Number(e.target.value))}
              required
            >
              <option value="">— выберите —</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name || s.login}</option>
              ))}
            </select>
          </label>
        )}

        <label>
          <span>Название *</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder='Например: "Розы — Пн/Чт"'
            required
          />
        </label>

        <fieldset className="fieldset">
          <legend>Даты отгрузок</legend>
          <p className="muted" style={{ marginTop: 0 }}>
            Список конкретных дат отгрузки. Для каждой укажи момент закрытия приёма заказов
            (например, за день до отгрузки). Время — МСК. Каждый час воркер пересчитывает
            «время до отгрузки» для опубликованных товаров.
          </p>
          {form.windows.length === 0 && (
            <p className="muted">Пока нет дат. Нажми «+ Добавить дату» ниже.</p>
          )}
          {form.windows.map((w, idx) => (
            <div className="row" key={idx} style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={{ flex: 1, minWidth: 220 }}>
                <span>Дата и время отгрузки (МСК)</span>
                <input
                  type="datetime-local"
                  value={w.ship_dt}
                  onChange={(e) => {
                    const next = [...form.windows];
                    next[idx] = { ...next[idx], ship_dt: e.target.value };
                    setForm({ ...form, windows: next });
                  }}
                  required
                />
              </label>
              <label style={{ flex: 1, minWidth: 220 }}>
                <span>Дедлайн приёма заказов (МСК)</span>
                <input
                  type="datetime-local"
                  value={w.deadline_dt ?? ''}
                  onChange={(e) => {
                    const next = [...form.windows];
                    next[idx] = { ...next[idx], deadline_dt: e.target.value || null };
                    setForm({ ...form, windows: next });
                  }}
                />
              </label>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setForm({ ...form, windows: form.windows.filter((_, i) => i !== idx) });
                }}
              >✕</button>
            </div>
          ))}
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn"
              onClick={() => {
                // Heuristic next slot: 10:00 a week ahead, deadline 24h earlier.
                const d = new Date(Date.now() + 7 * 86400 * 1000);
                d.setHours(10, 0, 0, 0);
                const pad = (n: number) => String(n).padStart(2, '0');
                const ship = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T10:00`;
                const dl = new Date(d.getTime() - 24 * 3600 * 1000);
                const deadline = `${dl.getFullYear()}-${pad(dl.getMonth() + 1)}-${pad(dl.getDate())}T${pad(dl.getHours())}:${pad(dl.getMinutes())}`;
                setForm({ ...form, windows: [...form.windows, { ship_dt: ship, deadline_dt: deadline }] });
              }}
            >+ Добавить дату</button>
            {form.windows.length > 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                Когда есть хотя бы одна дата — повторяющийся паттерн ниже игнорируется.
              </span>
            )}
          </div>
        </fieldset>

        <details className="fieldset" style={{ padding: 0 }}>
          <summary style={{ padding: '10px 16px', cursor: 'pointer', color: 'var(--muted)' }}>
            Дополнительно: повторяющийся паттерн (старая модель)
          </summary>
          <div style={{ padding: '0 16px 12px' }}>
          <p className="muted">
            Используется только если выше нет ни одной конкретной даты. Полезно для регулярных отгрузок без явного списка.
          </p>
          <fieldset className="fieldset">
          <legend>Отгрузка</legend>
          <div className="weekday-row">
            {WEEKDAYS_RU.map((w) => (
              <label key={w.iso} className={`chip ${form.ship_days.has(w.iso) ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={form.ship_days.has(w.iso)}
                  onChange={() => toggleSet('ship_days', w.iso)}
                />
                <span>{w.short}</span>
              </label>
            ))}
          </div>
          <label>
            <span>Время отгрузки (МСК)</span>
            <input
              type="time"
              value={form.ship_time_msk}
              onChange={(e) => setForm({ ...form, ship_time_msk: e.target.value })}
              required
            />
          </label>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Дедлайн приёма заказов</legend>
          <label>
            <span>Как считать</span>
            <select
              value={form.deadline_anchor}
              onChange={(e) => setForm({ ...form, deadline_anchor: e.target.value as 'absolute' | 'relative' })}
            >
              <option value="absolute">По дню недели (например, до Вс 23:59 перед отгрузкой)</option>
              <option value="relative">За N часов до отгрузки</option>
            </select>
          </label>

          {form.deadline_anchor === 'absolute' ? (
            <>
              <div className="weekday-row">
                {WEEKDAYS_RU.map((w) => (
                  <label key={w.iso} className={`chip ${form.deadline_days.has(w.iso) ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={form.deadline_days.has(w.iso)}
                      onChange={() => toggleSet('deadline_days', w.iso)}
                    />
                    <span>{w.short}</span>
                  </label>
                ))}
              </div>
              <label>
                <span>Время дедлайна (МСК)</span>
                <input
                  type="time"
                  value={form.deadline_time_msk}
                  onChange={(e) => setForm({ ...form, deadline_time_msk: e.target.value })}
                />
              </label>
            </>
          ) : (
            <label>
              <span>За сколько часов до отгрузки</span>
              <input
                type="number"
                min={1}
                max={336}
                value={form.deadline_offset_h}
                onChange={(e) => setForm({ ...form, deadline_offset_h: Number(e.target.value) })}
              />
            </label>
          )}
        </fieldset>
          </div>
        </details>

        <label className="row">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
          />
          <span>Использовать по умолчанию для этого продавца</span>
        </label>

        <label>
          <span>Заметки</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>

        {err && <div className="error">{err}</div>}

        <div className="row">
          <button className="btn primary" disabled={busy} type="submit">
            {busy ? 'Сохраняем…' : (isNew ? 'Создать' : 'Сохранить')}
          </button>
          {savedMsg && <span className="ok-msg">{savedMsg}</span>}
        </div>
      </form>

      {preview && (
        <div className="card sub" style={{ marginTop: 18 }}>
          <h3>Ближайшие окна (от {fmtMskHuman(preview.from)})</h3>
          {preview.windows.length === 0 ? (
            <p className="muted">Не удалось рассчитать — проверь, что выбран хотя бы один день отгрузки.</p>
          ) : (
            <table className="grid compact">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Дедлайн заказов</th>
                  <th>Отгрузка</th>
                </tr>
              </thead>
              <tbody>
                {preview.windows.map((w, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{fmtMskHuman(w.deadline)}</td>
                    <td>{fmtMskHuman(w.ship)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
